import { Injectable } from '@angular/core';
import {
    ApplicationPurposeCodeEnum,
    ApplicationPurposeCodeLabelMapping,
} from '../../application/shared/constants/application-purpose-codes';
import { ApplicationTypeLabelMapping } from '../../../core/constants/application-type-enum';
import {
    ServiceSelectionEnum,
    ServiceSelectionLabelMapping,
} from '../../../core/enums/applications/service-selection-enum';
import { DashboardConstants } from '../../dashboard/constants/dashboard-constants';
import { GridApplicationRowBase } from '../../dashboard/models/grid-application-row-base';
import { GridOptions } from 'ag-grid-community';
import { FilterOption } from '../../../core/models/grid/filter-option';
import {
    ApplicationFilingStatus800MHzHoldDayPattern,
    ApplicationFilingStatusEnum,
    ApplicationFilingStatusPSCADayPattern,
} from '../../application/shared/constants/application-filing-status';
import { PscaStatusEnum } from '../../application/shared/constants/psca-status';

@Injectable({
    providedIn: 'root',
})
export class ApplicationGridService {
    constructor() {}

    static formatServicesColumnWithOrder(
        gridParams: GridApplicationRowBase,
    ): string {
        return `${this.formatServicesColumn(gridParams)}${gridParams.orderId ? `\nOrder: ${gridParams.orderId}` : ''}`;
    }

    /**
     * Gets the list of application statuses that should be included in the status filter dropdown based on the applications currently in the grid.
     * This ensures that the filter options are always relevant to the data being displayed.
     */
    static getApplicableApplicationStatusFilterOptions(
        rowData: GridApplicationRowBase[],
    ): FilterOption[] {
        const statusesInGrid = new Set(rowData.map((row) => row.filingStatus));

        return Array.from(statusesInGrid)
            .sort(
                // Sort the statuses alphabetically, ignoring case
                new Intl.Collator(undefined, { sensitivity: 'base' }).compare,
            )
            .map((status) => ({
                label: status,
                value: status,
                selected: false,
            }));
    }

    static isHandedOverAmendment(gridParams: GridApplicationRowBase): boolean {
        return (
            gridParams.isHandedOver &&
            gridParams.isForm160 === false &&
            gridParams.applicationPurpose ===
                ApplicationPurposeCodeEnum.amendment
        );
    }

    static formatServicesColumn(gridParams: GridApplicationRowBase): string {
        if (gridParams.isForm160) {
            return DashboardConstants.FRN_REQUEST_LABEL;
        }

        let formattedServices = `${ApplicationPurposeCodeLabelMapping[gridParams.applicationPurpose as keyof typeof ApplicationPurposeCodeLabelMapping]}`;

        if (gridParams.applicationType) {
            formattedServices += ` (${ApplicationTypeLabelMapping[gridParams.applicationType]})`;
        }

        return formattedServices;
    }

    static getServiceValue(gridParams: GridApplicationRowBase): string {
        if (gridParams.isForm160) {
            return DashboardConstants.FRN_REQUEST_LABEL;
        }

        return ApplicationPurposeCodeLabelMapping[
            gridParams.applicationPurpose as keyof typeof ApplicationPurposeCodeLabelMapping
        ];
    }

    static formatServiceSelectionColumn(
        gridParams: GridApplicationRowBase,
    ): string {
        return ServiceSelectionLabelMapping[
            gridParams.doItYourselfAutocoord as ServiceSelectionEnum
        ];
    }

    static formatApplicantTransmissionLocationColumn(
        gridParams: GridApplicationRowBase,
    ): string {
        const parts = [];

        if (gridParams.licenseeEntityName) {
            parts.push(gridParams.licenseeEntityName);
        }

        const location = [gridParams.locationCity, gridParams.locationState]
            .filter(Boolean)
            .join(', ');

        if (location) {
            parts.push(location);
        }

        return parts.join(', ');
    }

    static formatCustomerToUserThenCompany(
        gridParams: GridApplicationRowBase,
    ): string {
        const parts = [];

        const customerName = [gridParams.firstName, gridParams.lastName]
            .filter(Boolean)
            .join(' ');

        if (customerName) {
            parts.push(customerName);
        }

        if (gridParams.organizationName) {
            parts.push(gridParams.organizationName);
        }

        return parts.join(', ');
    }

    static formatCallSignColumn(gridParams: GridApplicationRowBase): string {
        const callSign = gridParams.callSign
            ? String(gridParams.callSign).trim()
            : '';
        const fileNumber = gridParams.fileNumber
            ? String(gridParams.fileNumber).trim()
            : '';
        if (callSign && fileNumber) {
            return `${callSign}\n${fileNumber}`;
        }
        return callSign || fileNumber || '';
    }

    static formatApplicationIdColumn(
        gridParams: GridApplicationRowBase,
    ): string {
        // For Form160 applications, use applicationId instead of salmonLogId
        if (gridParams.isForm160) {
            return String(gridParams.applicationId);
        }
        return `${gridParams.salmonLogId}\n${gridParams.pscaId || ''}`;
    }

    static getHighlightingGridOptions<T>(
        shouldHighlightFn: (data: T) => boolean,
    ): GridOptions {
        return {
            suppressRowHoverHighlight: true, // Used to prevent the hover effect from overriding the error highlighting
            rowClassRules: {
                // Use rowClassRules over getRowClass. So it'll remove the class when condition is no longer true.
                'bg-grid-row-editing hover:bg-grid-row-hover-editing': (
                    params,
                ) => {
                    return shouldHighlightFn(params.data) === true;
                },
                'hover:bg-grid-row-hover-neutral': (params) => {
                    return shouldHighlightFn(params.data) === false;
                },
            },
        };
    }

    static getStatusFilterValue(
        rowData: GridApplicationRowBase | undefined,
    ): string {
        const filingStatus = rowData?.filingStatus ?? '';

        if (ApplicationFilingStatus800MHzHoldDayPattern.test(filingStatus)) {
            return ApplicationFilingStatusEnum.holdDayX;
        }

        if (ApplicationFilingStatusPSCADayPattern.test(filingStatus)) {
            return PscaStatusEnum.ISDayX;
        }

        return filingStatus;
    }
}
